import {
  Button,
  ButtonColor,
} from '@lmt/unicorn-shared/app/components/common/ui-kit/buttons/Button';
import { useInsertKeyHandler } from '@lmt/unicorn-shared/app/components/dictionaries/useInsertKeyHandler';
import React, { FC } from 'react';
import {
  useSubtenants,
  useSubtenantsDict,
} from '../../../features/subtenants/selectors';
import { useGlobalForms } from '@lmt/unicorn-shared/app/components/global-forms/useGlobalForms';
import { TableTitle } from '@lmt/unicorn-shared/app/components/common/table/TableTitle';
import { SubtenantsTable } from './components/SubtenantsTable';
import { useScopedTranslation } from '@lmt/unicorn-shared/app/helpers/localization/useScopedTranslation';

import tableStyles from '@lmt/unicorn-shared/app/components/common/table/TableTitle.module.scss';
import PlusIcon from '@lmt/unicorn-shared/app/icons/plus-blue.svg?react';
import { useEventCallback } from '@mui/material';
import { useClaims } from '@lmt/unicorn-shared/app/features/auth/operations';
import { QueryFactory } from '@lmt/shared-types';
import { useModal } from '../../common/ui-kit/modal/useModal';
import { convertToErrorString } from '@lmt/unicorn-shared/app/helpers/form/useErrorHandler';
import { confirmRemoveSubtenant } from './confirmations';
import { findKey } from 'lodash';
import { toast } from 'react-toastify';

export const SubtenantList: FC = () => {
  const i18next = useScopedTranslation('subtenants.list');
  const forms = useGlobalForms();
  const claims = useClaims();
  const modals = useModal();

  const subtenants = useSubtenants();
  const subtenantsDict = useSubtenantsDict();

  const openCreate = useEventCallback(() => {
    if (!claims.permissions.createSubtenant) return;
    forms.openSubtenantCreating();
  });

  const openEdit = useEventCallback((id: number) =>
    forms.openSubtenantEditing(id),
  );

  const onDelete = useEventCallback(async (id: number) => {
    const patients = await QueryFactory.PatientClient.getPatients(
      undefined,
      undefined,
      [id],
    );
    const subtenantName = subtenantsDict[id].name;
    const subtenantOptions = subtenants
      .filter((s) => s.id !== id)
      .map((s) => s.name);
    const result = await confirmRemoveSubtenant(
      modals,
      subtenantName,
      subtenantOptions,
      patients.totalCount > 0,
    );
    if (!result.removeSubtenant) {
      return;
    }
    try {
      if (result.movePatients) {
        if (!result.selectedSubtenant) return;
        const selectedSubtenantId = findKey(subtenantsDict, {
          name: result.selectedSubtenant,
        });
        if (!selectedSubtenantId) return;
        const processId = await QueryFactory.PatientClient.startMovePatients(
          id,
          parseInt(selectedSubtenantId),
        );
        const isSuccess = await forms.openMovePatients(processId);
        if (!isSuccess) {
          toast(i18next.t('toasts.patients_moved_conflicts'), {
            type: 'warning',
          });
          return;
        }
        toast(
          i18next.t('toasts.patients_moved_success', {
            subtenant1: subtenantName,
            subtenant2: result.selectedSubtenant,
          }),
          {
            type: 'success',
          },
        );
      }
      await QueryFactory.TenantClient.removeSubtenant(id);
      toast(
        i18next.t('toasts.subtenant_deleted_success', {
          subtenant: subtenantName,
        }),
        {
          type: 'success',
        },
      );
    } catch (err) {
      await modals.showError({ text: convertToErrorString(err) });
    }
  });

  useInsertKeyHandler(openCreate);

  return (
    <div
      className={tableStyles.pageContainer}
      data-test-id="table-page-container"
    >
      <div className={tableStyles.tableToolbar}>
        <TableTitle title={i18next.t('title')} count={subtenants.length} />
        {claims.permissions.createSubtenant && (
          <Button
            testId="plus-button"
            title={i18next.t('add_button')}
            color={ButtonColor.Primary}
            onClick={openCreate}
            iconComponent={<PlusIcon />}
          />
        )}
      </div>
      <div className={tableStyles.tableContainer}>
        <SubtenantsTable
          data={subtenants}
          noResults={false}
          onEdit={openEdit}
          onDelete={onDelete}
        />
      </div>
    </div>
  );
};
